import { useState, useEffect } from "react";
import { Search, Map, X, Phone, User, Calendar, CheckCircle, Clock, AlertTriangle, Building, FileText, Home, Hammer, Award, Banknote, Flag, ChevronRight } from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Couleurs pour les étapes
const ETAPE_COLORS = {
  completed: "bg-green-500",
  current: "bg-blue-500 animate-pulse",
  pending: "bg-slate-600",
  blocked: "bg-red-500"
};

// Configuration des étapes du parcours
const ETAPES_CONFIG = [
  { id: "datePriseRdv", label: "Prise de RDV", icon: Calendar, phase: "commercial" },
  { id: "dateR1", label: "R1", icon: User, phase: "commercial" },
  { id: "resultatAppelQualite", label: "Appel Qualité", icon: Phone, phase: "commercial", hasStatus: true },
  { id: "amo", label: "A.M.O.", icon: Building, phase: "amo", hasMandate: true },
  { id: "transfereAmo", label: "Transfert AMO", icon: ChevronRight, phase: "amo", hasDate: true },
  { id: "vtAmo", label: "VT A.M.O.", icon: Home, phase: "amo" },
  { id: "statutAmo", label: "Statut AMO", icon: FileText, phase: "amo", isStatus: true },
  { id: "etatDevis", label: "État du Devis", icon: FileText, phase: "amo", isStatus: true },
  { id: "dateR2", label: "R2", icon: User, phase: "r2", hasArchi: true },
  { id: "statutR2", label: "Statut R2", icon: CheckCircle, phase: "r2", isStatus: true },
  { id: "etatParticipationRac", label: "Participation RAC", icon: Banknote, phase: "financier", isStatus: true },
  { id: "dateDepotAnah", label: "Dépôt ANAH", icon: FileText, phase: "financier" },
  { id: "dateOctroi", label: "Octroi", icon: Award, phase: "financier" },
  { id: "typeFiAvance", label: "Type FI Avance", icon: Banknote, phase: "financier", isType: true },
  { id: "dateEncaissementAvance", label: "Encaissement Avance", icon: Banknote, phase: "financier" },
  { id: "accordChantier", label: "Chantier à Démarrer", icon: Hammer, phase: "chantier", isAccord: true },
  { id: "dateDebutChantier", label: "Début Chantier", icon: Hammer, phase: "chantier", hasEquipe: true },
  { id: "dateFinChantier", label: "Fin Chantier", icon: Flag, phase: "chantier" },
  { id: "dateReceptionChantier", label: "Réception Chantier", icon: CheckCircle, phase: "chantier" },
  { id: "reserveOuPas", label: "Réserves ?", icon: AlertTriangle, phase: "chantier", isReserve: true },
  { id: "dateLeveeReserves", label: "Levée Réserves", icon: CheckCircle, phase: "chantier", conditionalOn: "reserveOuPas" },
  { id: "typeFiSolde", label: "Type FI Solde", icon: Banknote, phase: "financier", isType: true },
  { id: "sav", label: "S.A.V.", icon: AlertTriangle, phase: "sav", isSav: true },
];

const PHASE_LABELS = {
  commercial: { label: "Phase Commerciale", color: "from-blue-600 to-blue-800" },
  amo: { label: "Phase A.M.O.", color: "from-purple-600 to-purple-800" },
  r2: { label: "Phase R2", color: "from-indigo-600 to-indigo-800" },
  financier: { label: "Phase Financière", color: "from-amber-600 to-amber-800" },
  chantier: { label: "Phase Chantier", color: "from-green-600 to-green-800" },
  sav: { label: "S.A.V.", color: "from-red-600 to-red-800" },
};

function ParcoursTimeline({ parcours, onClose }) {
  if (!parcours) return null;

  const { client, etapes } = parcours;
  
  // Déterminer l'état de chaque étape
  const getEtapeState = (etapeConfig) => {
    const value = etapes[etapeConfig.id];
    
    if (etapeConfig.isSav) {
      return { state: "pending", value: "À venir" };
    }
    
    if (etapeConfig.conditionalOn) {
      const conditionValue = etapes[etapeConfig.conditionalOn];
      if (!conditionValue || conditionValue.toLowerCase() === "non") {
        return { state: "skip", value: "N/A" };
      }
    }
    
    if (etapeConfig.isStatus || etapeConfig.isType || etapeConfig.isAccord || etapeConfig.isReserve) {
      if (value) {
        return { state: "completed", value };
      }
      return { state: "pending", value: "-" };
    }
    
    if (value) {
      return { state: "completed", value };
    }
    return { state: "pending", value: "-" };
  };

  // Trouver l'étape actuelle (première étape pending après les completed)
  let currentPhaseFound = false;
  const etapesWithState = ETAPES_CONFIG.map((config) => {
    const { state, value } = getEtapeState(config);
    let finalState = state;
    
    if (state === "pending" && !currentPhaseFound) {
      finalState = "current";
      currentPhaseFound = true;
    }
    
    return { ...config, state: finalState, value };
  });

  // Grouper par phase
  const phases = {};
  etapesWithState.forEach((etape) => {
    if (!phases[etape.phase]) {
      phases[etape.phase] = [];
    }
    phases[etape.phase].push(etape);
  });

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-700">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-900/50 to-orange-900/50 p-6 border-b border-amber-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center">
                <Map className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-amber-100">
                  📜 Parcours Client
                </h2>
                <p className="text-amber-200/70 text-lg">
                  {client.nom} {client.prenom} 
                  <span className="text-amber-300/50 ml-2">({client.dpt})</span>
                </p>
                {client.telephone && (
                  <p className="text-amber-300/60 text-sm flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {client.telephone}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-amber-200" />
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {Object.entries(phases).map(([phaseKey, phaseEtapes]) => {
              const phaseInfo = PHASE_LABELS[phaseKey];
              const completedCount = phaseEtapes.filter(e => e.state === "completed").length;
              const totalCount = phaseEtapes.filter(e => e.state !== "skip").length;
              
              return (
                <div key={phaseKey} className="relative">
                  {/* Phase Header */}
                  <div className={`bg-gradient-to-r ${phaseInfo.color} rounded-t-xl p-3 flex items-center justify-between`}>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                      {phaseInfo.label}
                    </h3>
                    <span className="text-white/80 text-xs bg-white/20 px-2 py-1 rounded-full">
                      {completedCount}/{totalCount}
                    </span>
                  </div>
                  
                  {/* Étapes de la phase */}
                  <div className="bg-slate-800/50 rounded-b-xl p-4 border border-t-0 border-slate-700/50">
                    <div className="space-y-3">
                      {phaseEtapes.map((etape, idx) => {
                        if (etape.state === "skip") return null;
                        
                        const IconComponent = etape.icon;
                        const isCompleted = etape.state === "completed";
                        const isCurrent = etape.state === "current";
                        const isBlocked = etape.value && etape.value.toLowerCase().includes("blocage");
                        
                        let dotColor = ETAPE_COLORS.pending;
                        if (isCompleted) dotColor = ETAPE_COLORS.completed;
                        if (isCurrent) dotColor = ETAPE_COLORS.current;
                        if (isBlocked) dotColor = ETAPE_COLORS.blocked;
                        
                        // Infos supplémentaires
                        let extraInfo = null;
                        if (etape.hasStatus && etapes.raisonBlocage) {
                          extraInfo = <span className="text-red-400 text-xs ml-2">⚠️ {etapes.raisonBlocage}</span>;
                        }
                        if (etape.hasMandate) {
                          extraInfo = <span className={`text-xs ml-2 ${etapes.amoMandate === 'OUI' ? 'text-green-400' : 'text-orange-400'}`}>
                            {etapes.amoMandate === 'OUI' ? '✓ Mandaté' : '○ Non mandaté'}
                          </span>;
                        }
                        if (etape.hasDate && etapes.dateTransfertAmo) {
                          extraInfo = <span className="text-blue-400 text-xs ml-2">📅 {etapes.dateTransfertAmo}</span>;
                        }
                        if (etape.hasArchi && etapes.archiEnCharge) {
                          extraInfo = <span className="text-purple-400 text-xs ml-2">👷 {etapes.archiEnCharge}</span>;
                        }
                        if (etape.hasEquipe && etapes.equipeChantier) {
                          extraInfo = <span className="text-green-400 text-xs ml-2">👥 {etapes.equipeChantier}</span>;
                        }
                        if (etape.isReserve) {
                          const hasReserve = etape.value && etape.value.toLowerCase() === "oui";
                          extraInfo = hasReserve 
                            ? <span className="text-red-400 text-xs ml-2">⚠️ Réserves présentes</span>
                            : <span className="text-green-400 text-xs ml-2">✓ Pas de réserves</span>;
                        }
                        
                        return (
                          <div 
                            key={etape.id}
                            className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                              isCurrent ? 'bg-blue-500/20 border border-blue-500/50' :
                              isCompleted ? 'bg-green-500/10' : 'bg-slate-700/30'
                            }`}
                          >
                            {/* Dot indicator */}
                            <div className={`w-4 h-4 rounded-full ${dotColor} flex-shrink-0`} />
                            
                            {/* Icon */}
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-green-600' : isCurrent ? 'bg-blue-600' : 'bg-slate-600'
                            }`}>
                              <IconComponent className="w-4 h-4 text-white" />
                            </div>
                            
                            {/* Label */}
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${isCompleted ? 'text-green-300' : isCurrent ? 'text-blue-300' : 'text-slate-400'}`}>
                                {etape.label}
                                {extraInfo}
                              </p>
                            </div>
                            
                            {/* Value */}
                            <div className="flex-shrink-0 text-right">
                              {etape.isSav ? (
                                <span className="text-xs text-slate-500 italic">À implémenter</span>
                              ) : isCompleted ? (
                                <span className="text-sm font-medium text-white bg-green-600/30 px-2 py-1 rounded">
                                  {etape.value}
                                </span>
                              ) : isCurrent ? (
                                <span className="text-sm text-blue-300 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> En attente
                                </span>
                              ) : (
                                <span className="text-sm text-slate-500">-</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ParcoursClient() {
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [parcours, setParcours] = useState(null);
  const [loadingParcours, setLoadingParcours] = useState(false);

  // Recherche des clients
  const searchClients = async (query) => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/parcours/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error("Error searching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  // Charger le parcours d'un client
  const loadParcours = async (rowNumber) => {
    setLoadingParcours(true);
    try {
      const response = await authFetch(`${API}/api/parcours/client/${rowNumber}`);
      const data = await response.json();
      setParcours(data);
    } catch (error) {
      console.error("Error loading parcours:", error);
    } finally {
      setLoadingParcours(false);
    }
  };

  // Recherche initiale au montage
  useEffect(() => {
    searchClients("");
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      searchClients(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleClientClick = (client) => {
    setSelectedClient(client);
    loadParcours(client.rowNumber);
  };

  // Couleur de l'étape actuelle
  const getEtapeColor = (etape) => {
    if (etape.includes("Terminé")) return "bg-green-500";
    if (etape.includes("Chantier")) return "bg-amber-500";
    if (etape.includes("Octroi") || etape.includes("Dépôt")) return "bg-purple-500";
    if (etape.includes("R2")) return "bg-indigo-500";
    if (etape.includes("AMO") || etape.includes("VT")) return "bg-blue-500";
    if (etape.includes("Qualité") || etape.includes("R1")) return "bg-cyan-500";
    return "bg-slate-500";
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 p-4 border-b border-amber-700/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <Map className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-amber-100">📜 Parcours Client</h3>
            <p className="text-xs text-amber-200/60">Visualisez l'avancement de vos dossiers</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-slate-700/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par nom, prénom, département ou téléphone..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
          />
        </div>
      </div>

      {/* Client List */}
      <div className="max-h-[400px] overflow-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-400">
            <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-2" />
            Chargement...
          </div>
        ) : clients.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            Aucun client trouvé
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {clients.map((client) => (
              <div
                key={client.rowNumber}
                className="p-3 hover:bg-slate-700/30 transition-colors flex items-center gap-3"
              >
                {/* Info client */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">
                      {client.nom} {client.prenom}
                    </p>
                    <span className="text-xs text-slate-500">({client.dpt})</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    {client.telephone ? (
                      <span className="text-xs text-blue-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {client.telephone}
                      </span>
                    ) : (
                      <span className="text-xs text-red-400">⚠️ Tél. manquant</span>
                    )}
                    {client.source && (
                      <span className="text-xs text-slate-500">{client.source}</span>
                    )}
                  </div>
                </div>

                {/* Étape actuelle */}
                <div className="flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full text-white ${getEtapeColor(client.etapeActuelle)}`}>
                    {client.etapeActuelle}
                  </span>
                </div>

                {/* Bouton Parcours */}
                <button
                  onClick={() => handleClientClick(client)}
                  disabled={loadingParcours && selectedClient?.rowNumber === client.rowNumber}
                  className="flex-shrink-0 p-2 bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors disabled:opacity-50"
                  title="Voir le parcours"
                >
                  {loadingParcours && selectedClient?.rowNumber === client.rowNumber ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Map className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Parcours */}
      {parcours && (
        <ParcoursTimeline 
          parcours={parcours} 
          onClose={() => {
            setParcours(null);
            setSelectedClient(null);
          }} 
        />
      )}
    </div>
  );
}

export default ParcoursClient;
